import React, { useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { formatToCustomLocale } from "../../../../utilities";
import { Container, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  updateDisplayedBalance,
  setHoldAndWinOutroCounterFinished,
} from "../../../../State/actions";
import {
  selectCurrentRound,
  selectCurrency,
  selectIsSpinning,
  selectCounterAnimationStarted,
  selectCriticalAnimationPlaying,
  selectFreeRoundsStatus,
  selectFreeRoundsEnded,
  selectFreeRoundsCompletionPending,
  selectHoldAndWinActive,
  selectHoldAndWinCurrentRound,
  selectWinAnimationsPlaying,
  selectIsAnimatedWin,
  selectIsHoldAndWinCompletionReload,
  selectShowHoldAndWinIntro,
  selectFeatureState,
  selectTriggerRoundWin,
} from "../../../../State/selectors";
import {
  selectHoldAndWinOutroCounterFinished,
  selectWinAnimationCounterFinished,
} from "../../../../State/Slices/animationsSlice";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
  TEXT_STYLE_CONFIG,
} from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { FEATURES_CONFIG } from "../../../../featuresConfig";

/**
 * WinDisplay - Win amount display component for regular gameplay
 *
 * Handles two modes:
 * 1. Regular win (single line "Win: X")
 * 2. H&W trigger round win (dual line "Total Win: X" + "Win: Y" after outro counter)
 *
 * Note: Free rounds use FreeRoundsWinDisplay component instead.
 *
 * @param {Object} toolbar - Toolbar reference for positioning
 */
const WinDisplay = ({ toolbar }) => {
  const dispatch = useDispatch();
  const currentRound = useSelector(selectCurrentRound);
  const currency = useSelector(selectCurrency);
  const isSpinning = useSelector(selectIsSpinning);
  const counterAnimationStarted = useSelector(selectCounterAnimationStarted);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const freeRoundsStatus = useSelector(selectFreeRoundsStatus);
  const freeRoundsEnded = useSelector(selectFreeRoundsEnded);
  const freeRoundsCompletionPending = useSelector(
    selectFreeRoundsCompletionPending,
  );
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const isAnimatedWin = useSelector(selectIsAnimatedWin);
  const isHoldAndWinCompletionReload = useSelector(
    selectIsHoldAndWinCompletionReload,
  );
  const showHoldAndWinIntro = useSelector(selectShowHoldAndWinIntro);
  const featureState = useSelector(selectFeatureState);
  const triggerRoundWin = useSelector(selectTriggerRoundWin);
  const holdAndWinOutroCounterFinished = useSelector(
    selectHoldAndWinOutroCounterFinished,
  );
  const winAnimationCounterFinished = useSelector(
    selectWinAnimationCounterFinished,
  );

  // True when the current spin is a H&W trigger (intro phase) - coins landed but feature hasn't started yet
  const isHoldAndWinTriggerRound = useMemo(
    () =>
      featureState?.type === "holdAndWin" && featureState?.phase === "intro",
    [featureState?.type, featureState?.phase],
  );

  // True when we are in the H&W active phase AND the trigger round had a regular win
  // This is the special dual-line mode
  const isHoldAndWinTriggerWithWin = useMemo(
    () => holdAndWinActive && triggerRoundWin > 0,
    [holdAndWinActive, triggerRoundWin],
  );

  // Latch ref: stays true once dual-line mode is entered, until next spin resets it.
  // This keeps the dual-line display visible during the exit scene transition and
  // in base game after H&W ends, until the player spins again.
  // Both set and clear happen at render-time (not in useEffect) so shouldShowDualLine
  // reacts in the same frame — no one-render delay or stale "Total Win: 0" flash.
  const wasHoldAndWinTriggerWithWinRef = useRef(false);
  if (isHoldAndWinTriggerWithWin) {
    wasHoldAndWinTriggerWithWinRef.current = true;
  } else if (triggerRoundWin === 0) {
    // Clear latch the moment Redux clears triggerRoundWin (happens on next spin).
    // This is the earliest reliable signal that the feature data has been wiped,
    // so shouldShowDualLine collapses to false in the same render frame.
    wasHoldAndWinTriggerWithWinRef.current = false;
  }

  // Latch ref: keeps the single-line "Win: X" visible for H&W trigger rounds that also
  // have an animated win (Big Win popup). The win animation closes before holdAndWinActive
  // becomes true, causing a brief gap where shouldShowWin is false. This latch bridges
  // that gap. It is only armed AFTER winAnimationCounterFinished becomes true (counter done),
  // so it does NOT fire early — it only takes over once the counter has already shown the win.
  const wasHoldAndWinTriggerRoundRef = useRef(false);
  if (isHoldAndWinTriggerRound && currentRound?.win && winAnimationCounterFinished) {
    // Arm the latch only once the win animation counter has finished
    wasHoldAndWinTriggerRoundRef.current = true;
  } else if (holdAndWinActive || (isSpinning && !holdAndWinActive)) {
    // Clear once dual-line takes over or on next spin
    wasHoldAndWinTriggerRoundRef.current = false;
  }

  const { device, orientation, getResponsiveConfig, uiSymbolScale } =
    useDimensions();

  // Track previous shouldShowWin state to detect when WinDisplay becomes visible
  const prevShouldShowWinRef = useRef(false);

  // Ref for Total Win container (for anime.js slide animation in dual-line mode)
  const totalWinContainerRef = useRef(null);
  const slideAnimationRef = useRef(null);

  // Get button dimensions using the same system as PopiClock - memoized
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Get unified win configuration (font multiplier, x, y positions) - memoized
  const winConfig = useMemo(() => {
    let fontMultiplier = 1.0;
    let x = 0;
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.05;
        x = toolbar.width / 2;
        y = -buttonConfig.height * 0.9;
      } else {
        fontMultiplier = 1.1;
        x = toolbar.width - buttonConfig.width * 8;
        y = toolbar.height / 2;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.7;
        x = toolbar.width / 2;
        y = -buttonConfig.height * 0.9;
      } else {
        fontMultiplier = 1.35;
        x = toolbar.width / 1.8;
        y = toolbar.height / 2;
      }
    } else {
      // Desktop
      fontMultiplier = 1.0;
      x = toolbar.width / 1.65;
      y = toolbar.height / 2;
    }

    return { fontMultiplier, x, y };
  }, [
    device,
    orientation,
    toolbar.width,
    toolbar.height,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  // Calculate font sizes - memoized
  const baseFontSize = 36;
  const FONT_SIZE = useMemo(() => {
    const responsiveFontSize = baseFontSize * winConfig.fontMultiplier;
    return Math.floor(responsiveFontSize * uiSymbolScale);
  }, [winConfig.fontMultiplier, uiSymbolScale]);

  const LABEL_FONT_SIZE = useMemo(
    () => Math.floor(FONT_SIZE * 0.95),
    [FONT_SIZE],
  );

  // Line spacing for dual-line mode (matches FreeRoundsWinDisplay)
  const lineSpacing = useMemo(() => Math.round(FONT_SIZE * 0.6), [FONT_SIZE]);

  // Reset component state when new spin begins
  useEffect(() => {
    if (isSpinning && !holdAndWinActive) {
      dispatch(setCounterAnimationStarted(false));
      dispatch(setCounterAnimationFinished(false));
      dispatch(setHoldAndWinOutroCounterFinished(false));
      prevShouldShowWinRef.current = false;
      // wasHoldAndWinTriggerWithWinRef is cleared at render-time above (same frame)
    }
  }, [isSpinning, holdAndWinActive, dispatch]);

  // On completion reload: the outro counter already finished before the reload.
  // Immediately restore the dual-line state so both lines show on mount.
  useEffect(() => {
    if (isHoldAndWinCompletionReload && triggerRoundWin > 0) {
      dispatch(setHoldAndWinOutroCounterFinished(true));
      wasHoldAndWinTriggerWithWinRef.current = true;
    }
  // Only run once when the component first sees these values on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHoldAndWinCompletionReload, triggerRoundWin]);

  // Cleanup slide animation on unmount
  useEffect(() => {
    return () => {
      if (slideAnimationRef.current) {
        slideAnimationRef.current.pause();
        slideAnimationRef.current = null;
      }
    };
  }, []);

  // Check if we have a Hold and Win win to display (after feature completes, non-trigger-with-win path)
  // Only for the case where there was NO regular win in the trigger round.
  // Also exclude the latched dual-line case (wasHoldAndWinTriggerWithWinRef) so the
  // single-line path never fires while we're still showing the dual-line display.
  const hasHoldAndWinWin = useMemo(
    () =>
      holdAndWinCurrentRound?.win > 0 &&
      !isHoldAndWinTriggerRound &&
      !isHoldAndWinTriggerWithWin &&
      !wasHoldAndWinTriggerWithWinRef.current,
    [
      holdAndWinCurrentRound?.win,
      isHoldAndWinTriggerRound,
      isHoldAndWinTriggerWithWin,
    ],
  );

  // Regular single-line shouldShowWin:
  // - Regular win (no H&W active, no H&W trigger)
  // - H&W trigger round before H&W starts (shows Win: 330 briefly)
  // - H&W win (no trigger win): show as soon as outro counter finishes, same timing as dual-line
  // - H&W win fallback: after feature + transition fully complete (existing path)
  // CRITICAL: !holdAndWinActive prevents the first branch from firing during H&W respins
  // CRITICAL: !wasHoldAndWinTriggerWithWinRef.current prevents the first branch from firing
  //           after H&W ends (latch still active) — dual-line handles display in that case
  const shouldShowWin = useMemo(
    () =>
      // Win celebration (Big/Mega/SuperMega): show as soon as counter finishes
      (winAnimationCounterFinished && currentRound?.win && !isSpinning) ||
      // H&W trigger round with animated win: keep single-line visible until dual-line takes over
      (wasHoldAndWinTriggerRoundRef.current && !holdAndWinActive && !isSpinning) ||
      // Regular win (no animated win, no H&W active)
      (counterAnimationStarted &&
        currentRound?.win &&
        !isSpinning &&
        !criticalAnimationPlaying &&
        !holdAndWinActive &&
        !wasHoldAndWinTriggerWithWinRef.current &&
        (!isAnimatedWin || !winAnimationsPlaying)) ||
      // Regular H&W (no trigger win): show Win as soon as outro counter finishes
      (hasHoldAndWinWin && holdAndWinOutroCounterFinished && !isSpinning) ||
      // Fallback: show after H&W fully exits (transition complete)
      (hasHoldAndWinWin &&
        !holdAndWinActive &&
        !showHoldAndWinIntro &&
        !isSpinning &&
        !criticalAnimationPlaying &&
        (!isAnimatedWin || !winAnimationsPlaying)),
    [
      counterAnimationStarted,
      currentRound?.win,
      isSpinning,
      criticalAnimationPlaying,
      hasHoldAndWinWin,
      holdAndWinActive,
      holdAndWinOutroCounterFinished,
      winAnimationCounterFinished,
      showHoldAndWinIntro,
      isAnimatedWin,
      winAnimationsPlaying,
    ],
  );

  // Dual-line mode: show Total Win during H&W active when trigger round had a regular win.
  // Also stays true via the latch after H&W ends (during exit transition and in base game)
  // until the next spin clears wasHoldAndWinTriggerWithWinRef.
  // triggerRoundWin > 0 guard prevents a brief "Total Win: 0" flash after the latch resets
  // but before Redux clears triggerRoundWin on the next spin.
  const shouldShowDualLine = useMemo(
    () =>
      triggerRoundWin > 0 &&
      (isHoldAndWinTriggerWithWin || wasHoldAndWinTriggerWithWinRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isHoldAndWinTriggerWithWin, triggerRoundWin],
  );

  // In dual-line mode, Total Win = trigger round win + H&W win (after counter finishes).
  // Uses shouldShowDualLine (which includes the latch) so the value persists after H&W ends.
  const totalWinAmount = useMemo(() => {
    if (!shouldShowDualLine) return 0;
    const hnwWin = holdAndWinOutroCounterFinished
      ? holdAndWinCurrentRound?.win || 0
      : 0;
    return triggerRoundWin + hnwWin;
  }, [
    shouldShowDualLine,
    holdAndWinOutroCounterFinished,
    holdAndWinCurrentRound?.win,
    triggerRoundWin,
  ]);

  // Use Hold and Win win if available (non-trigger-with-win path), otherwise regular win
  const displayWin = useMemo(
    () => (hasHoldAndWinWin ? holdAndWinCurrentRound.win : currentRound.win),
    [hasHoldAndWinWin, holdAndWinCurrentRound?.win, currentRound?.win],
  );

  // Balance update for regular single-line wins (non H&W trigger with win path)
  useEffect(() => {
    if (shouldShowWin && !prevShouldShowWinRef.current) {
      const isFreeRoundsActive =
        freeRoundsStatus === "active" || freeRoundsStatus === "played";
      const shouldSkipBalanceUpdate =
        isHoldAndWinCompletionReload ||
        isFreeRoundsActive ||
        isHoldAndWinTriggerRound ||
        wasHoldAndWinTriggerRoundRef.current || // Skip - trigger round latch, balance handled by H&W flow
        isHoldAndWinTriggerWithWin || // Skip - balance handled by scene transition exit
        (hasHoldAndWinWin && holdAndWinOutroCounterFinished) || // Skip - balance handled by refreshGameData after transition
        winAnimationCounterFinished || // Skip - balance handled by refreshGameData in finishAnimation
        isAnimatedWin; // Skip - balance handled by refreshGameData when win animation closes

      if (!shouldSkipBalanceUpdate) {
        dispatch(updateDisplayedBalance());
      }
    }

    prevShouldShowWinRef.current = shouldShowWin;
  }, [
    shouldShowWin,
    dispatch,
    isHoldAndWinCompletionReload,
    freeRoundsStatus,
    isHoldAndWinTriggerRound,
    isHoldAndWinTriggerWithWin,
    counterAnimationStarted,
    hasHoldAndWinWin,
    holdAndWinOutroCounterFinished,
    winAnimationCounterFinished,
    holdAndWinActive,
    criticalAnimationPlaying,
    winAnimationsPlaying,
    isAnimatedWin,
  ]);

  // Balance for the H&W trigger-with-win path is handled by SceneTransitionOverlay,
  // which dispatches refreshGameData() on exit — that API response sets displayedBalance
  // directly from the real server balance. No manual dispatch needed here.

  // Animate Total Win container position when H&W win sub-line appears (dual-line mode)
  useEffect(() => {
    if (!totalWinContainerRef.current || !shouldShowDualLine) return;

    if (slideAnimationRef.current) {
      slideAnimationRef.current.pause();
      slideAnimationRef.current = null;
    }

    if (holdAndWinOutroCounterFinished) {
      // Sub-line appeared → slide Total Win UP
      slideAnimationRef.current = anime({
        targets: totalWinContainerRef.current,
        y: -lineSpacing,
        duration: 250,
        easing: "easeOutCubic",
        update: (anim) => {
          if (!totalWinContainerRef.current) anim.pause();
        },
      });
    } else {
      // No sub-line → Total Win centered
      totalWinContainerRef.current.y = 0;
    }

    return () => {
      if (slideAnimationRef.current) {
        slideAnimationRef.current.pause();
        slideAnimationRef.current = null;
      }
    };
  }, [holdAndWinOutroCounterFinished, shouldShowDualLine, lineSpacing]);

  // Text style objects - memoized
  const winLabelStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: LABEL_FONT_SIZE,
        fontWeight: "400",
        fill: "#ffffff",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [LABEL_FONT_SIZE],
  );

  const winStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FONT_SIZE,
        fontWeight: "700",
        fill: "#ffbf11",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [FONT_SIZE],
  );

  const currencyStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FONT_SIZE,
        fontWeight: "700",
        fill: "#ffbf11",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [FONT_SIZE],
  );

  // Format text values
  const winText = useMemo(() => formatToCustomLocale(displayWin), [displayWin]);
  const totalWinText = useMemo(
    () => formatToCustomLocale(totalWinAmount),
    [totalWinAmount],
  );
  const hnwWinText = useMemo(
    () => formatToCustomLocale(holdAndWinCurrentRound?.win || 0),
    [holdAndWinCurrentRound?.win],
  );
  const currencyText = useMemo(() => ` ${currency}`, [currency]);
  const winLabelText = "Win: ";
  const totalWinLabelText = "Total Win: ";

  // Text metrics for single-line mode - memoized
  const singleLineMetrics = useMemo(() => {
    const winLabelMetrics = PIXI.TextMetrics.measureText(
      winLabelText,
      winLabelStyle,
    );
    const winTextMetrics = PIXI.TextMetrics.measureText(winText, winStyle);
    const currencyTextMetrics = PIXI.TextMetrics.measureText(
      currencyText,
      currencyStyle,
    );
    const totalTextWidth =
      winLabelMetrics.width + winTextMetrics.width + currencyTextMetrics.width;

    return {
      winLabelMetrics,
      winTextMetrics,
      currencyTextMetrics,
      totalTextWidth,
    };
  }, [winLabelText, winText, currencyText, winLabelStyle, winStyle, currencyStyle]);

  // Text metrics for dual-line mode - memoized
  const dualLineMetrics = useMemo(() => {
    const totalWinLabelMetrics = PIXI.TextMetrics.measureText(
      totalWinLabelText,
      winLabelStyle,
    );
    const totalWinTextMetrics = PIXI.TextMetrics.measureText(
      totalWinText,
      winStyle,
    );
    const currencyTextMetrics = PIXI.TextMetrics.measureText(
      currencyText,
      currencyStyle,
    );
    const hnwWinTextMetrics = PIXI.TextMetrics.measureText(
      hnwWinText,
      winStyle,
    );
    const winLabelMetrics = PIXI.TextMetrics.measureText(
      winLabelText,
      winLabelStyle,
    );

    const totalWinLineWidth =
      totalWinLabelMetrics.width +
      totalWinTextMetrics.width +
      currencyTextMetrics.width;
    const hnwWinLineWidth =
      winLabelMetrics.width + hnwWinTextMetrics.width + currencyTextMetrics.width;

    return {
      totalWinLabelMetrics,
      totalWinTextMetrics,
      currencyTextMetrics,
      hnwWinTextMetrics,
      winLabelMetrics,
      totalWinLineWidth,
      hnwWinLineWidth,
    };
  }, [
    totalWinLabelText,
    totalWinText,
    hnwWinText,
    currencyText,
    winLabelText,
    winLabelStyle,
    winStyle,
    currencyStyle,
  ]);

  // Hide during free rounds (FreeRoundsWinDisplay handles everything)
  if (
    FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
    (freeRoundsStatus !== null ||
      freeRoundsEnded ||
      freeRoundsCompletionPending)
  ) {
    return null;
  }

  // Dual-line mode: H&W active with trigger round win
  if (shouldShowDualLine) {

    const {
      totalWinLabelMetrics,
      totalWinTextMetrics,
      currencyTextMetrics,
      hnwWinTextMetrics,
      winLabelMetrics,
      totalWinLineWidth,
      hnwWinLineWidth,
    } = dualLineMetrics;

    return (
      <Container
        name="secondary_win_display"
        x={winConfig.x}
        y={winConfig.y}
        zIndex={4}
      >
        {/* Total Win line (slides up when H&W win sub-line appears) */}
        <Container ref={totalWinContainerRef} name="total_win_line" y={0}>
          <Text
            key="total_win_label"
            text={totalWinLabelText}
            style={winLabelStyle}
            anchor={0.5}
            x={Math.round(-totalWinLineWidth / 2 + totalWinLabelMetrics.width / 2)}
          />
          <Text
            key={`total_win_amount_${totalWinText}`}
            text={totalWinText}
            style={winStyle}
            anchor={0.5}
            x={Math.round(
              -totalWinLineWidth / 2 +
                totalWinLabelMetrics.width +
                totalWinTextMetrics.width / 2,
            )}
          />
          <Text
            key="total_win_currency"
            text={currencyText}
            style={currencyStyle}
            anchor={0.5}
            x={Math.round(totalWinLineWidth / 2 - currencyTextMetrics.width / 2)}
          />
        </Container>

        {/* H&W Win sub-line (only after outro counter finishes) */}
        {holdAndWinOutroCounterFinished && (
          <Container name="hnw_win_line" y={lineSpacing}>
            <Text
              key="hnw_win_label"
              text={winLabelText}
              style={winLabelStyle}
              anchor={0.5}
              x={Math.round(-hnwWinLineWidth / 2 + winLabelMetrics.width / 2)}
            />
            <Text
              key={`hnw_win_amount_${hnwWinText}`}
              text={hnwWinText}
              style={winStyle}
              anchor={0.5}
              x={Math.round(
                -hnwWinLineWidth / 2 +
                  winLabelMetrics.width +
                  hnwWinTextMetrics.width / 2,
              )}
            />
            <Text
              key="hnw_win_currency"
              text={currencyText}
              style={currencyStyle}
              anchor={0.5}
              x={Math.round(
                hnwWinLineWidth / 2 - currencyTextMetrics.width / 2,
              )}
            />
          </Container>
        )}
      </Container>
    );
  }

  // Hide during free rounds (FreeRoundsWinDisplay handles that)
  if (
    FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
    (freeRoundsStatus !== null ||
      freeRoundsEnded ||
      freeRoundsCompletionPending)
  ) {
    return null;
  }

  // Single-line mode: regular win or H&W completion win (no trigger win)
  if (!shouldShowWin) {
    return null;
  }

  const {
    winLabelMetrics,
    winTextMetrics,
    currencyTextMetrics,
    totalTextWidth,
  } = singleLineMetrics;

  return (
    <Container
      name="secondary_win_display"
      x={winConfig.x}
      y={winConfig.y}
      zIndex={4}
    >
      {/* Win label text (white, static) */}
      <Text
        key={winLabelText}
        text={winLabelText}
        style={winLabelStyle}
        anchor={0.5}
        x={
          -totalTextWidth / 2 +
          winLabelMetrics.width / 2
        }
      />
      {/* Win amount text (golden) */}
      <Text
        key={winText}
        text={winText}
        style={winStyle}
        anchor={0.5}
        x={
          -totalTextWidth / 2 +
          winLabelMetrics.width +
          winTextMetrics.width / 2
        }
      />
      {/* Currency text (golden, static) */}
      <Text
        key={currencyText}
        text={currencyText}
        style={currencyStyle}
        anchor={0.5}
        x={
          totalTextWidth / 2 -
          currencyTextMetrics.width / 2
        }
      />
    </Container>
  );
};

export default WinDisplay;
